/**
 * Copyright 2023-2025 Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, nanoid, PayloadAction , createSelector} from "@reduxjs/toolkit";
import { RootState, store } from "..";
import { siteService } from "../../services/site-service";
import { RadioGroup, PlanMapData, defaultRadioGroup, GroupPhaseType } from "../../types/radioGroup";
import { ObjectReference } from "../../types/dataObject";
import Radio, { RadioX } from "../../types/radio";
import { fetchRadios } from "./radioSlice";
import { Types } from "../../constants/types";
import { Box, Circle, convertSvgToUrl, MarkerData, RadioPin } from "@h9/ui-lib";
import { BAICELLS, CARRIER_MODE, COLOR, HIGHWAY9, MIN_SITE_RADIUS, RADIO_TRIGGER_RADIUS } from "~/constants";
import { calcLengths, groupBy } from "~/helpers/utils";
import { checkStatus, getBandChannels } from "~/views/radios/radioHelper";
import { getRadioIconType } from "~/helpers/radioHelper";
import { PlanCoods } from "~/components/shared/d3/FloorPlan";
import { geoDistance } from "~/helpers/geoDistance";
import { defaultSite } from "~/types/site";
import { PlanSelection } from "~/helpers/overlayImages";

type Coods = {
  radioID: string;
  groupID: string;
  radioCoordinates: {
    lat: number;
    lng: number;
  };
};

type mappingID_with_APCount = {
  [key: string]: number;
};
type mappingStatus = {
  [key: string]: string;
};

type initState = {
  open: boolean;
  groupEditFromExcelImport: boolean;
  edit: boolean;
  current: RadioGroup;
  data: RadioGroup[];
  other?: any;
  status: string;
  channelPlanOpen: boolean;

  // from radio groups
  selectedIDs: string[];
  radioMappingIds?: mappingID_with_APCount;
  connectedRadioMappingIds?: mappingID_with_APCount;
  radioMappingStatus?: mappingStatus;

  // from edges
  showAdvanceEdges: boolean;

  // Track updated overlay images to avoid sending unchanged image data to backend
  updatedOverlayImages?: PlanSelection[] | null;
};



export const initialState: initState = {
  open: false,
  groupEditFromExcelImport: false,
  edit: false,
  status: "idle",
  current: defaultRadioGroup,
  data: [],
  channelPlanOpen: false,

  // from radio groups
  selectedIDs: [Types.all],
  radioMappingIds: {},
  connectedRadioMappingIds: {},
  radioMappingStatus: {},
  showAdvanceEdges: false,

  // Track updated overlay images to avoid sending unchanged image data to backend
  updatedOverlayImages: null,
};

export const fetchRadioGroups = createAsyncThunk("radioGroup/fetchRadioGroups", async (query: { timestamp?: number }, thunk) => {
  const timestamp = query?.timestamp;
  const queryString = timestamp ? { timestamp } : {};

  const _radioGroups = (await siteService.getRadioGroups(queryString)).sort((a, b) => a.name.localeCompare(b.name));
  const state = thunk.getState() as RootState;
  let radios = state.radio.data;
  // add the radios to the radio groups
  if (!radios?.length) {
    const response = await thunk.dispatch(fetchRadios({ timestamp }));
    if (response.meta.requestStatus === "fulfilled") {
      radios = response.payload as Radio[];
    }
  }
  const data = await updateRadioGroups(_radioGroups, radios);
  return data;
});

// MCS-3778: Radio groups created by RAN_KPI_ONLY users land in the backend without `sas`,
// `frequency`, and `datapath` (those fields are not in the KPI create-payload whitelist).
// When an operator edits such a group, reducers that mutate nested fields under those keys
// would crash on `state.current.<missing>.<field> = ...`. Lazy-init each missing object from
// defaultRadioGroup the first time it's written, matching the existing `setCenter` pattern
// for `plan`. Structured-clone so we don't share refs with the module-level default.
function ensureKpiGroupDefaults(current: RadioGroup) {
  if (!current.sas) current.sas = structuredClone(defaultRadioGroup.sas);
  if (!current.frequency) current.frequency = structuredClone(defaultRadioGroup.frequency);
  if (!current.datapath) current.datapath = structuredClone(defaultRadioGroup.datapath);
}

export const radioGroupSlice = createSlice({
  name: Types.radioGroup,
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setGroupEditFromExcelImport: (state, action: PayloadAction<boolean>) => {
      state.groupEditFromExcelImport = action.payload;
    },
    setEdit: (state, action: PayloadAction<boolean>) => {
      state.edit = action.payload;
    },
    setValues: (state, action: PayloadAction<RadioGroup>) => {
      state.current = action.payload;
    },
    setData: (state, action: PayloadAction<RadioGroup[]>) => {
      state.data = action.payload;
    },
    updateData: (state, action: PayloadAction<RadioGroup>) => {
      const index = state.data.findIndex((item) => item.id === action.payload.id);
      state.data[index] = action.payload;
    },

    setChannelPlanOpen: (state, action: PayloadAction<boolean>) => {
      state.channelPlanOpen = action.payload;
    },

    setSelectedIds: (state, action: PayloadAction<string[]>) => {
      state.selectedIDs = action.payload;
    },
    setRadioMappingIds: (state, action: PayloadAction<mappingID_with_APCount>) => {
      state.radioMappingIds = action.payload;
    },
    setConnectedRadioMappingIds: (state, action: PayloadAction<mappingID_with_APCount>) => {
      state.connectedRadioMappingIds = action.payload;
    },
    setMappingStatus: (state, action: PayloadAction<mappingStatus>) => {
      state.radioMappingStatus = action.payload;
    },

    setRadioCoordinates: (state, action: PayloadAction<Coods>) => {
      const { radioID, groupID, radioCoordinates } = action.payload;
      // find the group
      const group = state.data.find((grp) => grp.id === groupID);
      if (group) {
        // update the radio coordinates location
        group.radioCoordinates = group.radioCoordinates?.map((coods) => {
          if (coods.id === radioID) {
            return {
              ...coods,
              location: radioCoordinates,
            };
          }
          return coods;
        });

        // update the state data with the new group data
        state.data = state.data.map((grp) => {
          if (grp.id === groupID) {
            return group;
          }
          return grp;
        });
      }
    },

    addRadioCoordinates: (
      state,
      action: {
        payload: {
          groupID: string;
          marker: MarkerData;
        };
      }
    ) => {
      const { groupID, marker } = action.payload;
      // find the group
      const group = state.data.find((grp) => grp.id === groupID);

      if (group) {
        // update the radio coordinates location
        group.radioCoordinates = group.radioCoordinates?.concat(marker);
        // update the state data with the new group data
        state.data = state.data.map((grp) => {
          if (grp.id === groupID) {
            return group;
          }
          return grp;
        });
      }
    },

    // edit values
    setName: (state, action: PayloadAction<string>) => {
      state.current.name = action.payload;
    },
    setZoneType: (state, action: PayloadAction<string>) => {
      state.current.zoneType = action.payload;
    },
    setGroupPhaseLifecycleStage: (state, action: PayloadAction<GroupPhaseType>) => {
      state.current.lifecycleStage = action.payload;
    },
    setTechnology: (state, action: PayloadAction<string>) => {
      state.current.technology = action.payload;
      const BANDS_5G = ["N48", "N77", "N78"];
      const BANDS_4G = ["B48"];
      const band = state.current.technology === "5G" ? BANDS_5G : BANDS_4G
      ensureKpiGroupDefaults(state.current);
      if (!band.includes(state.current.frequency.band)) {
        state.current.frequency.band = band[0]
      }
    },
    setMobilityProfile: (state, action: PayloadAction<ObjectReference | null>) => {
      state.current.mobilityProfile = action.payload;
    },

    setManualProfileSelection: (state, action: PayloadAction<boolean>) => {
      state.current._manualProfileSelection = action.payload;
    },

    setRadioSiteId: (state, action: PayloadAction<string>) => {
      state.current.radioSite.id = action.payload;
    },
    setRadioSiteName: (state, action: PayloadAction<string>) => {
      state.current.radioSite.name = action.payload;
    },

    setMacroEnbIdList: (state, action: PayloadAction<number[]>) => {
      state.current.macroEnbIdList = action.payload;
    },

    setOverrideMacroEnbIdList: (state, action: PayloadAction<boolean>) => {
      state.current.overrideMacroEnbIdList = action.payload;
    },

    setLocalExitEnabled: (state, action: PayloadAction<boolean>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.datapath.localExit.enabled = action.payload;
    },
    setLocalExitMode: (state, action: PayloadAction<string>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.datapath.localExit.mode = action.payload;
    },
    setLocalExitIpPool: (state, action: PayloadAction<string>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.datapath.localExit.ipPool = action.payload;
    },
    setLocalExitIpPoolNetmask: (state, action: PayloadAction<string>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.datapath.localExit.ipPoolNetmask = action.payload;
    },

    setSasEnabled: (state, action: PayloadAction<boolean>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.sas.sasEnabled = action.payload;
    },
    setSasUserId: (state, action: PayloadAction<string>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.sas.sasUserId = action.payload;
    },
    setFrequencySelectionLogic: (state, action: PayloadAction<string>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.sas.frequencySelectionLogic = action.payload;
    },
    setIsOnlyAllowSelectedFrequency: (state, action: PayloadAction<boolean>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.sas.allowOnlySelectedFrequency = action.payload;
    },

    setFrequencyBand: (state, action: PayloadAction<string>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.frequency.band = action.payload;
    },
    setFrequencyBandwidth: (state, action: PayloadAction<number>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.frequency.bandwidth = action.payload;
    },
    setFrequencySubFrameAssignment: (state, action: PayloadAction<number>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.frequency.subframeAssignment = action.payload;
    },
    setFrequencySpecialSubFramePattern: (state, action: PayloadAction<number>) => {
      ensureKpiGroupDefaults(state.current);
      state.current.frequency.specialSubframePattern = action.payload;
    },

    setShowAdvanceEdges: (state, action: PayloadAction<boolean>) => {
      state.showAdvanceEdges = action.payload;
    },

    // map tab
    setFloor: (state, action: PayloadAction<number | undefined>) => {
      state.current.floor = action.payload;
    },
    setArea: (state, action: PayloadAction<number | undefined>) => {
      state.current.area = action.payload;
    },
    setCenter: (state, action: PayloadAction<{ lat: number; lng: number; address?: string }>) => {
      if (state.current.plan) {
        state.current.plan.calibrationPoints.map.center = {
          latitude: action.payload.lat,
          longitude: action.payload.lng,
          name: action.payload.address,
        };
      } else {
        state.current.plan = defaultRadioGroup.plan;
      }
    },
    setFloorPlanImage: (
      state,
      action: PayloadAction<{
        data: string;
        name: string;
      }>
    ) => {
      if (state.current.plan?.calibrationPoints.floorPlan) {
        state.current.plan.calibrationPoints.floorPlan.imageData = action.payload.data;
        state.current.plan.calibrationPoints.floorPlan.imageName = action.payload.name;
      } else {
        if (state.current.plan) {
          state.current.plan.calibrationPoints.floorPlan = {
            imageData: action.payload.data,
            imageName: action.payload.name,
          };
        } else {
          state.current.plan = defaultRadioGroup.plan;
        }
      }
    },

    setFloorPlanCoods: (state, action: PayloadAction<PlanCoods[]>) => {
      state.current.radioFloorPlanCoods = action.payload;
    },

    clearFloorPlanImage: (state) => {
      if (state.current.plan?.calibrationPoints.floorPlan) {
        delete state.current.plan.calibrationPoints.floorPlan.imageData;
        delete state.current.plan.calibrationPoints.floorPlan.imageId;
        delete state.current.plan.calibrationPoints.floorPlan.imageName;
      }
    },

    setRFPlanImage: (
      state,
      action: PayloadAction<{
        data: string;
        name: string;
      }>
    ) => {
      if (state.current.plan?.calibrationPoints.rfPlan) {
        state.current.plan.calibrationPoints.rfPlan.imageData = action.payload.data;
        state.current.plan.calibrationPoints.rfPlan.imageName = action.payload.name;
      } else {
        if (state.current.plan) {
          state.current.plan.calibrationPoints.rfPlan = {
            imageData: action.payload.data,
            imageName: action.payload.name,
          };
        } else {
          state.current.plan = defaultRadioGroup.plan;
        }
      }
    },

    clearRFPlanImage: (state) => {
      if (state.current.plan?.calibrationPoints.rfPlan) {
        delete state.current.plan.calibrationPoints.rfPlan.imageData;
        delete state.current.plan.calibrationPoints.rfPlan.imageId;
        delete state.current.plan.calibrationPoints.rfPlan.imageName;
      }
    },

    setMapZoom: (state, action: PayloadAction<number>) => {
      if (state.current.plan) {
        state.current.plan.calibrationPoints.map.zoom = action.payload;
      } else {
        state.current.plan = defaultRadioGroup.plan;
      }
    },
    setMapType: (state, action: PayloadAction<string>) => {
      if (state.current.plan) {
        state.current.plan.calibrationPoints.map.view = action.payload?.toUpperCase();
      } else {
        state.current.plan = defaultRadioGroup.plan;
      }
    },
    setMapData: (state, action: PayloadAction<PlanMapData>) => {
      if (state.current.plan) {
        state.current.plan.calibrationPoints.map = action.payload;
      } else {
        state.current.plan = defaultRadioGroup.plan;
      }
    },
    setPlanFromMapOverlay: (state, action: PayloadAction<RadioGroup["plan"]>) => {
      if (state.current.plan) {
        state.current.plan = { ...state.current.plan,  ...action.payload};
      } else {
        state.current.plan = {...defaultRadioGroup.plan, ...action.payload};
      }
    },

    setOther: (state, action: PayloadAction<Object>) => {
      state.other = {
        ...state.other,
        ...action.payload,
      };
    },

    setLocationSyncMode: (state, action: PayloadAction<string>) => {
      state.current.locationSyncMode = action.payload;
    },

    setSynchronizationProfile: (state, action: PayloadAction<ObjectReference | undefined>) => {
      state.current.clockPool = action.payload;
    },

    setUpdatedOverlayImages: (state, action: PayloadAction<PlanSelection[] | null>) => {
      state.updatedOverlayImages = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRadioGroups.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRadioGroups.fulfilled, (state, action) => {
        // console.log("fetchRadioGroups.fulfilled", action.payload);
        state.data = action.payload;
        // sync the current radio group with the data if it exists and is not open
        if (!state.open && state.current?.id) {
          const current = state.data.find((item) => item.id === state.current.id);
          if (current) { state.current = current; }
        }
        state.status = "idle";
      })
      .addCase(fetchRadioGroups.rejected, (state) => {
        state.status = "error";
      });
  },
});

export default radioGroupSlice.reducer;
export const radioGroupActions = radioGroupSlice.actions;

export const radioGroupState = (state: RootState) => state.radioGroup.current;
export const radioGroupOpen = (state: RootState) => state.radioGroup.open;
export const groupEditFromExcelImport = (state: RootState) => state.radioGroup.groupEditFromExcelImport;
export const radioGroupEdit = (state: RootState) => state.radioGroup.edit;
export const radioGroupData = (state: RootState) => state.radioGroup.data;
export const radioGroupOther = (state: RootState) => state.radioGroup.other;
export const radioGroupById = (id: string) => (state: RootState) => state.radioGroup.data.find((item) => item.id === id) || null;
export const radioChannelPlanOpen = (state: RootState) => state.radioGroup.channelPlanOpen;

export const radioGroupSelected = (state: RootState) => state.radioGroup.selectedIDs;
export const radioGroupMappingIds = (state: RootState) => state.radioGroup.radioMappingIds;
export const radioGroupConnectedMappingIDs = (state: RootState) => state.radioGroup.connectedRadioMappingIds;
export const radioGroupMappingStatus = (state: RootState) => state.radioGroup.radioMappingStatus;
export const radioGroupShowAdvanceEdges = (state: RootState) => state.radioGroup.showAdvanceEdges;
export const radioGroupLoading = (state: RootState) => state.radioGroup.status === "loading";
export const radioGroupUpdatedOverlayImages = (state: RootState) => state.radioGroup.updatedOverlayImages;

export const radioGroupHas5GEdge =  createSelector(
    (state: RootState) => state.radioGroup.current,
    (state: RootState) => state.edge.data,
    (state: RootState) => state.edge.edgesWithPool,
    (state: RootState) => state.site.data,
    (currentGroup, edgeList, edgesWithPools, siteData) => {
      const radioGroupSiteId = currentGroup?.radioSite?.id;
      // Find the site from siteData using the radioSite id
      const site = siteData.find((s) => s.id === radioGroupSiteId);

      // If the site is not found, then return false
      if (!site) {
        return false;
      }
      const edges = site.edges || [];

      // Find the selected edge pool id
      const selectedEdgePoolId = edges.find(edge => edge?.edge?.id?.startsWith("edgepool-"))?.edge?.id;

      // Find the selected edge pool technology
      const selectedEdgePoolTechnology = selectedEdgePoolId 
      ? edgesWithPools.find(edge => edge.edgePoolId === selectedEdgePoolId)?.networkType 
      : undefined;

      // Check if any edge has 5G network type or if the selected edge pool technology is 5G
      return edges.some((e) => {
        const _edge = edgeList.find((edge) => edge.id === e.edge.id);
        return _edge?.networkType === "5G" || selectedEdgePoolTechnology === "5G";
      });
    }
);


export function updateRadioGroups(Groups: RadioGroup[], radios: Radio[]) {
  // Bucket radios by group id once (O(radios)) instead of scanning the whole radios array
  // for every group (O(groups x radios)). Fewer temporary iterator/array allocations per
  // refresh -> less GC pressure (MCS-3969).
  const radiosByGroup = new Map<string, Radio[]>();
  for (const radio of radios) {
    const gid = radio.radioGroup?.id;
    if (!gid) continue;
    const bucket = radiosByGroup.get(gid);
    if (bucket) bucket.push(radio);
    else radiosByGroup.set(gid, [radio]);
  }
  return Promise.all(Groups.map(async (grp) => {
    const _radios = grp.id ? radiosByGroup.get(grp.id) ?? [] : [];
    const _group: RadioGroup = { ...grp, radios: _radios };

    const coods: MarkerData[] = [];
    const planCoods: PlanCoods[] = [];
    const plmnList: string[] = [];
    const vendorList: string[] = [];

    // Calculate fallback positions
    let fallbackIndex = 0;
    const radiosWithoutFloorPlan = _radios.filter((radio) => !radio.floorPlan?.xOffset || !radio.floorPlan?.yOffset);
    const baseMultiplier = _radios.length > 1 ? 55 - Math.min(50, _radios.length * 2) : 55;
    const offsetMultiplier = radiosWithoutFloorPlan.length > 1 ? baseMultiplier + (200 / radiosWithoutFloorPlan.length) : baseMultiplier;

    // Process all radios in parallel
    await Promise.all(_radios.map(async (radio) => {
      if (!radio) return;

      const marker = generateRadioPin(radio, grp);


      const planCood: PlanCoods = {
        x: radio.floorPlan?.xOffset ?? (500 + fallbackIndex * offsetMultiplier * (-1) ** fallbackIndex),
        y: radio.floorPlan?.yOffset ?? (500 + fallbackIndex * offsetMultiplier * (-1) ** fallbackIndex),
        id: radio.id ?? nanoid(),
        icon: marker?.icon,
        title: radio.name,
      };

      if (!radio.floorPlan?.xOffset || !radio.floorPlan?.yOffset) {
        fallbackIndex++;
      }

      if (marker) coods.push(marker);
      planCoods.push(planCood);

      if (radio.runtimeInfo?.plmnList) {
        plmnList.push(...radio.runtimeInfo.plmnList);
      }
      if (radio.runtimeInfo?.vendorType) {
        vendorList.push(radio.runtimeInfo.vendorType === BAICELLS ? HIGHWAY9 : radio.runtimeInfo.vendorType);
      }
    }));

    return {
      ..._group,
      radioCoordinates: coods,
      radioFloorPlanCoods: planCoods,
      _plmnList: Array.from(new Set(plmnList)),
      _vendorList: Array.from(new Set(vendorList))
    };
  }));
}

export function syncRadioStatusAndConnectedWithGroups(data: RadioX[]) {
  const dispatch = store.dispatch;
  // set the grp status and ids
  const groupedRows = groupBy(data, "radioGroup.id");
  const grpStatus: { [key: string]: string } = {};
  const connectedApsObj: { [key: string]: number } = {};
  for (const key in groupedRows) {
    if (Object.hasOwnProperty.call(groupedRows, key)) {
      const radios = groupedRows[key];
      const status = radios?.map((radio) => radio.statusColor); // [COLOR.GREEN, COLOR.GREEN, COLOR.RED]
      grpStatus[key] = checkStatus(status);

      // get count of green status
      const connectedCount = status.filter((item) => item === COLOR.GREEN).length;
      connectedApsObj[key] = connectedCount;
    }
  }
  const grpLengthObj = calcLengths(groupedRows);
  dispatch(radioGroupActions.setRadioMappingIds(grpLengthObj));
  dispatch(radioGroupActions.setConnectedRadioMappingIds(connectedApsObj));
  dispatch(radioGroupActions.setMappingStatus(grpStatus));
}


export function getRadioCircles(grp: RadioGroup, radios: Radio[]) {
  const circles: Circle[] = [];

      // if zone type is indoor, then add a circle
      if (grp.zoneType?.toLowerCase() === "indoor" && radios.length > 1) {

        // get the center of the circle from the radios
        let count = 0;
        const centroid = radios.reduce((acc, radio) => {
          if (!radio.location?.latitude || !radio.location?.longitude) return acc;
          count++;
          return {
            lat: acc.lat + radio.location?.latitude,
            lng: acc.lng + radio.location?.longitude,
          }
        }, { lat: 0, lng: 0 });
        centroid.lat /= count;
        centroid.lng /= count;
        // calculate the max distance of the circle from the center to the farthest radio
        let maxDistance = MIN_SITE_RADIUS;
        for (const radio of radios) {
          // if the radio is not located, then skip it
          if (radio.location?.latitude === 0 && radio.location?.longitude === 0) continue;

          const distance = geoDistance(centroid?.lat ?? 0, centroid?.lng ?? 0, radio.location?.latitude ?? 0, radio.location?.longitude ?? 0);
          maxDistance = Math.max(maxDistance, distance);
        }

        const data : Circle = {
          center: {
            lat: centroid?.lat ?? 0,
            lng: centroid?.lng ?? 0,
        },
        radius: maxDistance + RADIO_TRIGGER_RADIUS,
        color: '#226a58',
        count: radios.length,
        title: grp.name,
        tooltipRender: (circle) => {
          // const areaInFeet = (Math.PI * (circle.radius ** 2)) * 10.7639;
          // const { value, notation } = formatCompactNumberInParts(areaInFeet);
          return <Box p={1}>
            <Box style={{ textTransform: "capitalize", fontSize: '12px', fontWeight: 'bold' }}>
              {circle.title}
              <br />
              <span style={{color: '#ccc', fontSize: '12px'}}>Count - {circle.count} APs</span>
            </Box>
            <div style={{ marginLeft: 10}}>
              {radios.map((radio) => {
                return <div key={radio.id}>
                  <span style={{ fontSize: '12px'}}>
                    &bull; {radio.name}
                  </span>
                </div>
              })}
            </div>
          </Box>
          }
        }

        circles.push(data);
      }
      return circles;
}

// Radio pins are rebuilt for every radio on every realtime refresh. Serializing the SVG
// each time (renderToString + XMLSerializer) is pure churn when the pin's inputs are
// unchanged, so cache the resulting icon URL keyed by its exact visual inputs. Bounded
// FIFO so the cache itself can't grow without limit (MCS-3969).
const radioPinIconCache = new Map<string, string>();
const RADIO_PIN_CACHE_MAX = 3000;
function cachedIcon(key: string, build: () => string): string {
  let url = radioPinIconCache.get(key);
  if (url === undefined) {
    url = build();
    radioPinIconCache.set(key, url);
    if (radioPinIconCache.size > RADIO_PIN_CACHE_MAX) {
      const oldest = radioPinIconCache.keys().next().value;
      if (oldest !== undefined) radioPinIconCache.delete(oldest);
    }
  }
  return url;
}

export function generateRadioPin(radio: Radio, grp?: RadioGroup) {

  if (!radio) return;


  const zoneType = getRadioIconType(radio);

  const location = {
    lat: radio?.location?.latitude ?? grp?.plan?.calibrationPoints?.map?.points[0]?.latitude ?? 0,
    lng: radio?.location?.longitude ?? grp?.plan?.calibrationPoints?.map?.points[0]?.longitude ?? 0,
  };

  // if the location is 0, 0
  //  then use the radio group center as the location of the radio
  //  if the radio group is not found, then use the default site center as the location of the radio
  if (location.lat === 0 && location.lng === 0) {
    location.lat = grp?.plan?.calibrationPoints?.map?.center?.latitude ?? defaultSite?.location?.latitude ?? 0;
    location.lng = grp?.plan?.calibrationPoints?.map?.center?.longitude ?? defaultSite?.location?.longitude ?? 0;
  }

  const bandChannels = getBandChannels((radio.runtimeInfo?.cellParameters?.[0].bandwidth ?? radio.cellParameters?.[0].bandwidth)!, radio.cellParameters?.[0].band);
  const channel = bandChannels.find((channel) => channel.earfcn === (radio.runtimeInfo?.cellParameters?.[0].earfcn ?? radio.cellParameters?.[0].earfcn?.[0]));

  const singleParams = {
    angle: radio.cellParameters?.[0].azimuth ?? 0,
    beamwidth: radio.cellParameters?.[0].beamwidth ?? 0,
    gain: (radio.runtimeInfo?.cellParameters?.[0].power ?? radio.cellParameters?.[0].power ?? 0) + (radio.cellParameters?.[0].gain ?? 0),
    health: radio.runtimeInfo?.overallHealth ?? "UNKNOWN",
    channelColor: channel?.color.slice(0, 7) ?? '#D3D3D3',
    network: radio.technology,
  };
  const isBorderRadio = radio.isBorderRadio ?? false;
  let icon = cachedIcon(`s|${zoneType}|${isBorderRadio}|${JSON.stringify(singleParams)}`, () =>
    convertSvgToUrl(<RadioPin type={zoneType} parameters={singleParams} isBorderRadio={isBorderRadio} />));

  if (radio.carrierMode !== CARRIER_MODE.SINGLE_CARRIER) {
    const sectors = radio.cellParameters?.map((cell , index) => {
      const runtimeCell = radio.runtimeInfo?.cellParameters?.[index];
      const bandChannels = getBandChannels((runtimeCell?.bandwidth ?? cell.bandwidth)!, (cell.band)!);
      const channel = bandChannels.find((channel) => channel.earfcn === (runtimeCell?.earfcn ?? cell.earfcn?.[0]));
      const EIRP = (runtimeCell?.power ?? cell.power ?? 0) + (cell.gain ?? 0);
      return ({
        angle: cell.azimuth ?? 0,
        beamwidth: cell.beamwidth ?? 0,
        gain:  radio.carrierMode === CARRIER_MODE.DUAL_CARRIER ? EIRP : index === 0 ? EIRP : EIRP / 4,
        channelColor: channel?.color.slice(0, 7) ?? '#D3D3D3'
      })
    }) ?? [];
    icon = cachedIcon(`m|${zoneType}|${isBorderRadio}|${radio.technology}|${radio.runtimeInfo?.overallHealth ?? "UNKNOWN"}|${JSON.stringify(sectors)}`, () =>
      convertSvgToUrl(<RadioPin type={zoneType} parameters={{
        sectors,
        health: radio.runtimeInfo?.overallHealth ?? "UNKNOWN",
        network : radio.technology,
      }} isBorderRadio={isBorderRadio} />));
  }

  const marker : MarkerData = {
    id: radio.id ?? nanoid(),
    location,
    title: radio.name,
    icon,
    radioType: zoneType,
    metadata: radio.cellParameters?.map((cell, index) => {
      const runtimeCell = radio.runtimeInfo?.cellParameters?.[index];
      return({
      "EARFCN": runtimeCell?.earfcn ?? cell.earfcn?.[0] ?? 0,
      "PCI":  cell.pci ?? 0,
      "Azi°": `${cell.azimuth ?? 0}°`,
      "BmW": `${cell.beamwidth ?? 0}°`,
      "Gain": `${cell.gain ?? 0}dBi`,
      "Power": `${runtimeCell?.power ?? cell.power ?? 0}dBm`,
      "Height": `${cell.altitude?.toFixed(2) ?? 0}m`,
    })})
  }

  return marker;
}
